import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import type { CatalogTableColumnsFunc } from '@backstage/plugin-catalog';
import { DefaultFilters } from '@backstage/plugin-catalog-react';
import { Chip } from '@material-ui/core';
import { getEnvironmentsFromEntity } from './components/catalog/envUtils';
import { EnvironmentPicker } from './components/catalog/EnvironmentPicker';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import {
  ConditionalEnvironmentFieldExtension,
  ConditionalGitRefFieldExtension,
  BulkAgentSelectorFieldExtension,
  BulkWorkspaceIdsFieldExtension,
  AgentNameFieldExtension,
  PerEnvFieldsFieldExtension,
  EnvVarsFieldExtension,
  ResourcesFieldExtension,
} from './scaffolder_extensions/FieldsExtension';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { oktaAuthApiRef } from '@backstage/core-plugin-api';

const catalogColumns: CatalogTableColumnsFunc = ctx => [
  ...CatalogTable.defaultColumnsFunc(ctx),
  {
    title: 'Environments',
    field: 'environments',
    render: row => {
      const envs = getEnvironmentsFromEntity(row.entity);
      if (!envs.length) return null;
      return (
        <>
          {envs.map(env => (
            <Chip key={env} label={env} size="small" style={{ margin: 1 }} />
          ))}
        </>
      );
    },
    sorting: false,
  },
];

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        providers={[
          // 'guest', //ONLY ADD FOR TESTING, IT WILL ALLOW ANYONE TO LOGIN AS A GUEST
          {
            id: 'okta-auth-provider',
            title: 'Okta',
            message: 'Sign in using Okta',
            apiRef: oktaAuthApiRef,
          },
        ]}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          columns={catalogColumns}
          filters={
            <>
              <DefaultFilters />
              <EnvironmentPicker />
            </>
          }
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          groups={[
            {
              title: 'Manage AI Agents',
              filter: e =>
                e?.metadata?.tags?.some(t => t.includes('agent')) ?? false,
            },
            {
              title: 'Manage Services',
              filter: e =>
                e?.metadata?.tags?.some(
                  t => t === 'api-server' || t === 'service',
                ) ?? false,
            },
          ]}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <ConditionalGitRefFieldExtension />
        <ConditionalEnvironmentFieldExtension />
        <AgentNameFieldExtension />
        <BulkAgentSelectorFieldExtension />
        <BulkWorkspaceIdsFieldExtension />
        <PerEnvFieldsFieldExtension />
        <EnvVarsFieldExtension />
        <ResourcesFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
